import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "@/components/Header";
import OrderChat from "@/components/OrderChat";
import ReviewForm from "@/components/ReviewForm";
import ReviewCard, { type ReviewData } from "@/components/ReviewCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, MapPin, Calendar, Package, Receipt, MessageCircle, ChevronDown, ChevronUp, Truck } from "lucide-react";
import { LiveDeliveryTracker } from "@/components/LiveDeliveryTracker";
import { DeliveryProofCard } from "@/components/DeliveryProofCard";
import { useLocation } from "wouter";
import type { Order, OrderLine, OrderFulfillmentSelection } from "@shared/schema";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/hooks/useCart";
import { apiRequest } from "@/lib/queryClient";
import { stripDemoMarker } from "@/lib/demo";
import { formatOrderStatus } from "@/lib/orderStatus";
import { ServiceFeeInfo } from "@/components/ServiceFeeInfo";

type OrderLineWithSeller = OrderLine & { sellerId: string | null };

// The step list is keyed off deliveryMethod, not the current status. Deriving
// it from status meant a community-delivery order showed the pickup steps
// ("Ready for pickup" → "Picked up") until a driver collected it, at which
// point the last two steps silently became "Out for delivery" → "Delivered" —
// so the buyer was told to go get an order that was being driven to them.
// Community delivery is the only flow with both handoffs: the seller marks it
// ready, then the driver picks up and delivers.
function getOrderProgressSteps(
  status: string | null | undefined,
  deliveryMethod: string | null | undefined,
) {
  const normalized = String(status || "").trim().toLowerCase();
  const method = String(deliveryMethod || "").trim().toLowerCase();

  const steps =
    method === "community_delivery"
      ? [
          { key: "paid", label: "Paid" },
          { key: "preparing", label: "Preparing" },
          { key: "ready_for_pickup", label: "Ready for driver" },
          { key: "out_for_delivery", label: "Out for delivery" },
          { key: "delivered", label: "Delivered" },
        ]
      : method === "seller_delivery"
        ? [
            { key: "paid", label: "Paid" },
            { key: "preparing", label: "Preparing" },
            { key: "out_for_delivery", label: "Out for delivery" },
            { key: "delivered", label: "Delivered" },
          ]
        : [
            { key: "paid", label: "Paid" },
            { key: "preparing", label: "Preparing" },
            { key: "ready_for_pickup", label: "Ready for pickup" },
            { key: "picked_up", label: "Picked up" },
          ];

  const currentIndex = steps.findIndex((step) => step.key === normalized);

  // pending, cancel_requested, cancelled, refunded and refund_required aren't
  // points on this line. Rendering the tracker anyway drew every step greyed
  // out, which reads as "nothing has happened yet" on an order that was in
  // fact refunded. The status pill above already names the real state.
  if (currentIndex < 0) return [];

  return steps.map((step, index) => ({
    ...step,
    active: index <= currentIndex,
    current: currentIndex === index,
  }));
}

function OrderReviewSection({ orderId, canReview }: { orderId: string; canReview: boolean }) {
  const { data, isLoading, refetch } = useQuery<{ review: ReviewData | null }>({
    queryKey: ["/api/reviews/order", orderId],
    queryFn: async () => {
      const res = await fetch(`/api/reviews/order/${orderId}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: Boolean(orderId),
  });

  if (isLoading) return null;

  const existingReview = data?.review ?? null;

  if (existingReview) {
    return (
      <Card className="text-left mb-8">
        <CardContent className="pt-6">
          <div className="text-sm font-semibold mb-3">Your Review</div>
          <ReviewCard review={existingReview} />
        </CardContent>
      </Card>
    );
  }

  if (canReview) {
    return (
      <div className="text-left mb-8">
        <ReviewForm orderId={orderId} onSuccess={() => { void refetch(); }} />
      </div>
    );
  }

  return null;
}

export default function OrderConfirmation() {
  const [, setLocation] = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const orderId = searchParams.get("orderId");

  // Fetch order details. Polls every 10 s so status changes
  // (preparing → ready_for_pickup → out_for_delivery → delivered) and
  // the delivery proof appear on the confirmation page without the
  // buyer having to leave it.
  const { data: order, refetch } = useQuery<Order & { fulfillmentSelections?: OrderFulfillmentSelection[]; buyerName?: string }>({
    queryKey: ["/api/orders", orderId],
    enabled: !!orderId,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  });

  const { data: orderLines = [] } = useQuery<OrderLineWithSeller[]>({
    queryKey: ["/api/orders", orderId, "lines"],
    queryFn: async () => {
      const res = await fetch(`/api/orders/${orderId}/lines`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!orderId,
  });

  const { user } = useAuth();
  const currentUserId = user?.id;
  const { clearCart } = useCart();
  const [expandedSellerId, setExpandedSellerId] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) {
      setLocation("/");
    }
  }, [orderId, setLocation]);

  useEffect(() => {
    if (!orderId) return;
    window.dispatchEvent(new CustomEvent("mission:checkout-fulfillment"));
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    let lastCheckoutOrderId: string | null = null;
    try {
      lastCheckoutOrderId = sessionStorage.getItem("marketlink.lastCheckoutOrderId");
    } catch { /* private mode */ }
    if (lastCheckoutOrderId !== orderId) return;

    (async () => {
      try {
        await apiRequest("POST", `/api/orders/${orderId}/mark-paid`);
      } catch { /* already paid, stock conflict, or network — surfaced by order status */ }
      if (cancelled) return;
      try {
        sessionStorage.removeItem("marketlink.lastCheckoutOrderId");
      } catch { /* noop */ }
      clearCart();
      void refetch();
    })();

    return () => {
      cancelled = true;
    };
  }, [orderId, clearCart, refetch]);

  useEffect(() => {
    if (!orderId) return;

    const refreshOrder = () => {
      void refetch();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshOrder();
      }
    };

    window.addEventListener("focus", refreshOrder);
    window.addEventListener("pageshow", refreshOrder);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshOrder);
      window.removeEventListener("pageshow", refreshOrder);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [orderId, refetch]);

  const subtotal = order ? parseFloat(order.subtotal) : 0;
  const platformFee = order ? parseFloat(order.platformFee) : 0;
  const deliveryFee = order && order.deliveryFee ? parseFloat(order.deliveryFee) : 0;
  const total = order ? parseFloat(order.total) : 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 text-primary mb-6">
            <CheckCircle className="h-12 w-12" />
          </div>

          {/* Success Message */}
          <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-confirmation-title">
            Order Confirmed!
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            Thank you for supporting local sellers. Your order has been successfully placed.
          </p>

          {/* Delivery tracker — keyed on orderId so a delayed or failed order
              fetch doesn't hide the route map after the push-notification tap.
              The tracker self-renders nothing when there's no delivery request. */}
          {orderId && <LiveDeliveryTracker orderId={orderId} />}
          {orderId && order?.status && (
            <DeliveryProofCard orderId={orderId} orderStatus={order.status} />
          )}

          {order && (
            <Card className="text-left mb-8">
              <CardContent className="pt-6 space-y-6">
                {/* Order Number */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Order Number</p>
                    <p className="font-mono font-semibold" data-testid="text-order-id">
                      {order.id.substring(0, 8).toUpperCase()}
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <Receipt className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-semibold mb-1">Status</p>
                      <p className="text-sm text-muted-foreground" data-testid="text-order-status">
                        {formatOrderStatus(order.status)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Order Progress */}
                {(() => {
                  const progressSteps = getOrderProgressSteps(
                    order.status,
                    order.deliveryMethod,
                  );
                  if (progressSteps.length === 0) return null;
                  return (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Order Progress</p>
                      <div className="grid gap-2 sm:grid-cols-5">
                        {progressSteps.map((step) => (
                          <div
                            key={step.key}
                            className={`rounded-lg border px-3 py-2 text-center text-xs sm:text-sm ${
                              step.current
                                ? "border-primary bg-primary/10 text-foreground"
                                : step.active
                                  ? "border-primary/30 bg-primary/5 text-foreground"
                                  : "border-border bg-background text-muted-foreground"
                            }`}
                            data-testid={`order-progress-${step.key}`}
                          >
                            {step.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Pickup Details */}
                {(() => {
                  const selections = order.fulfillmentSelections ?? [];
                  if (selections.length > 1) {
                    return (
                      <div className="space-y-3">
                        <p className="font-semibold">Pickup / delivery per seller</p>
                        <div className="space-y-2">
                          {selections.map((selection) => {
                            const locationLine = selection.dropZoneName
                              ? [
                                  selection.dropZoneName,
                                  selection.dropZoneCity,
                                  selection.dropZoneState,
                                ]
                                  .filter(Boolean)
                                  .join(", ")
                              : selection.buyerLabel;
                            return (
                              <div
                                key={selection.id}
                                className="rounded-lg border bg-muted/40 p-3"
                                data-testid={`selection-${selection.sellerId}`}
                              >
                                <p className="font-medium text-sm">
                                  {stripDemoMarker(selection.sellerName) || selection.sellerName}
                                </p>
                                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="mt-0.5 h-4 w-4 text-primary" />
                                    <p className="text-sm text-muted-foreground">{locationLine}</p>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Calendar className="mt-0.5 h-4 w-4 text-primary" />
                                    <p className="text-sm text-muted-foreground">
                                      {selection.scheduledDate} · {selection.timeWindowLabel}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-primary mt-0.5" />
                        <div>
                          <p className="font-semibold mb-1">Pickup Location</p>
                          <p className="text-sm text-muted-foreground">
                            {order.pickupLocation}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Calendar className="h-5 w-5 text-primary mt-0.5" />
                        <div>
                          <p className="font-semibold mb-1">Pickup Window</p>
                          <p className="text-sm text-muted-foreground">
                            {order.pickupWindow}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Order Total */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      Service fee
                      <ServiceFeeInfo subtotal={subtotal} />
                    </span>
                    <span>${platformFee.toFixed(2)}</span>
                  </div>
                  {deliveryFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Delivery Fee</span>
                      <span>${deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-lg border-t pt-2">
                    <span>Total Paid</span>
                    <span data-testid="text-total-paid">${total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
                  <Package className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold mb-1">What's Next?</p>
                    <p className="text-muted-foreground">
                      Your items will be ready for pickup during the scheduled window.
                      Sellers receive the product price minus a 1% fee — 0% for founding makers.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Chat with the order's counterparties. Buyers see one card per
              non-self seller; chat is hidden until they've paid (pending,
              cancel_requested, cancelled, refunded all suppressed).
              Sellers viewing their own order see one card for the buyer
              regardless of payment status — they need to be able to nudge
              an unpaid buyer or coordinate on a cancel-requested order.
              Orders that only contain the buyer's own listings show no chat. */}
          {order && (() => {
              type Counterparty = { id: string; name: string };
              const counterparties: Counterparty[] = [];
              const isBuyer = currentUserId === order.buyerId;
              const status = String(order.status || "").trim().toLowerCase();
              const buyerSuppressed =
                isBuyer &&
                ["cancel_requested", "refunded", "pending", "cancelled"].includes(status);
              const sellerSuppressed =
                !isBuyer && ["refunded"].includes(status);
              if (buyerSuppressed || sellerSuppressed) return null;
              if (isBuyer) {
                const seen = new Set<string>();
                for (const line of orderLines) {
                  if (!line.sellerId || line.sellerId === currentUserId) continue;
                  if (seen.has(line.sellerId)) continue;
                  seen.add(line.sellerId);
                  counterparties.push({
                    id: line.sellerId,
                    name: stripDemoMarker(line.sellerName) || line.sellerName || "Seller",
                  });
                }
              } else if (currentUserId) {
                const isSellerOnOrder = orderLines.some(
                  (line) => line.sellerId === currentUserId,
                );
                if (isSellerOnOrder) {
                  counterparties.push({
                    id: order.buyerId,
                    name: order.buyerName || "Buyer",
                  });
                }
              }
              if (counterparties.length === 0) return null;
              return (
                <Card className="text-left mb-8">
                  <CardContent className="pt-4 pb-4 divide-y">
                    {counterparties.map(({ id, name }) => {
                      const isOpen = expandedSellerId === id;
                      return (
                        <div key={id} className="py-3 first:pt-0 last:pb-0">
                          <button
                            type="button"
                            className="flex w-full items-center justify-between text-left"
                            onClick={() =>
                              setExpandedSellerId((prev) =>
                                prev === id ? null : id,
                              )
                            }
                          >
                            <span className="inline-flex items-center gap-2 text-sm font-semibold">
                              <MessageCircle className="h-4 w-4 text-primary" />
                              Chat with {name}
                            </span>
                            {isOpen ? (
                              <ChevronUp className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                          {isOpen && (
                            <div className="mt-3">
                              <OrderChat
                                orderId={order.id}
                                otherPartyName={name}
                                recipientId={id}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })()}

          {/* Review section */}
          {order && orderId && (() => {
            const status = String(order.status || "").trim().toLowerCase();
            const canReview = status === "picked_up" || status === "delivered";
            return <OrderReviewSection orderId={orderId} canReview={canReview} />;
          })()}

          {/* Actions */}
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              size="lg"
              onClick={() => setLocation("/orders")}
              data-testid="button-view-my-orders"
            >
              View My Orders
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setLocation("/")}
              data-testid="button-continue-shopping"
            >
              Continue Shopping
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setLocation("/seller")}
              data-testid="button-seller-dashboard"
            >
              Go to Grower / Maker Dashboard
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
